import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  StyledButtonSmall,
  StyledSelectInput,
  StyledText,
} from 'components/Wizard/Global/WizardStyled';
import { isEqual, last } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { clearAll, integrateFileText } from 'utils/apiFilesUploadHelpers';
import { selectTourById, setSolutionParam, setTourParameter } from 'actions';
import { useCookies } from 'react-cookie';
import { colors } from 'global/variables';
import withTranslation from '../../../hoc/withTranslation';
import {
  StyledContrastPanelHeader,
  StyledContrastPanelHeaderText,
} from '../../Wizard/Global/WizardStyledContrastPanel';
import {
  adjustDownloadGeoJSON,
  getPredefinedFiles,
  getLastValidIteration,
  hasOngoingAsyncRequest,
  isIterationSolution,
  removeProperties,
  getToursDataFromIteration,
  fetchPredefinedFileList,
} from '../../../utils/SolutionHelpers';
import { APP_MODES, getAppMode } from '../../../utils/urlHelpers';
import { StyledCloseButton } from '../Menu/Menu';
import { isProdEnv } from '../../../utils/helpers';

const { white } = colors;

const StyledWrapper = styled.div(() => ({
  display: 'flex',
  flexDirection: 'row',
  height: '1.3rem',
  alignItems: 'center',
}));

const StyledOption = styled.option(() => ({
  color: white,
  '&:hover': {
    backgroundColor: 'green',
  },
}));

const appMode = getAppMode();
const isProd = isProdEnv();
const showFullDev = appMode.includes(APP_MODES.DEVELOPER, APP_MODES.WIP);

const OPTIONS = {
  PROBLEM: 'problem',
  SOLUTION: 'solution',
  GEO: 'geoJson',
  INTERNAL_TOUR_PLANNER: 'tourPlanner',
  INTERNAL_USER: 'user',
  INTERNAL_ITERATIONS: 'iterations',
};
export const METHODS = {
  MANUAL: 'manual',
  DEMO: 'demo',
  IMPORT: 'import',
  JSON_IMPORT: 'json_import',
};

const DevConsoleHeader = ({
  translations: {
    error,
    devConsoleLabels: {
      configDesc,
      solutionUpload,
      configTitle,
      optionSolution,
      optionProblem,
      optionGeo,
      showPredefinedFiles,
      hidePredefinedFiles,
    },
  },
  setJsonObject,
  selectedOption,
  setSelectedOption,
  togglePanel,
  showPanel,
  errorState,
  jsonObject,
  justPastedJson,
  setJustPastedJson,
}) => {
  const dispatch = useDispatch();

  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const filtered = getLastValidIteration(solution.requests[solution.show]);
  const tourData = getToursDataFromIteration(filtered);
  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const { display } = useSelector(({ mapContainer }) => mapContainer);
  const currentStep = useSelector((state) => state.usageContext.currentStep);
  const oAuth = useSelector(({ oAuth: stateOAuth }) => stateOAuth);
  const handleSelectTourById = useCallback((data) => dispatch(selectTourById(data)), [dispatch]);
  const [cookies] = useCookies(['apikey']);
  const [PREDEFINED_FILES, setPredefinedFiles] = useState([]);
  const user = useSelector(({ user: stateUser }) => stateUser);
  const [isSolution, setIsSolution] = useState();
  const [hasProblem, setHasProblem] = useState();
  const [fileNames, setFileNames] = useState({ filename: '', problemFileName: '' });
  const [predefinedFileName, setPredefinedFileName] = useState();
  const [predefinedFile, setPredefinedFile] = useState();
  const [showPredefined, setShowPredefined] = useState(false);
  const convertToTerritory = user.mapSettings.convertToTerritory;
  const vehicleLimits = user.mapSettings.addVehicleLimit;
  const vhcl = tourPlanner.vehicles?.[0];
  const limits = vhcl?.limits || {};
  const anyVehicleLimitUserChange =
    limits.maxDistance?.userChange === true ||
    limits.shiftTime?.userChange === true ||
    limits.maxDrivingTime?.userChange === true ||
    limits.maxStops?.userChange === true ||
    limits.minStops?.userChange === true ||
    limits.stops?.userChange === true;
  const sfUserChange = user.mapSettings.speedFactor?.userChange;
  const speedFactor = user.mapSettings.speedFactor?.enabled;
  const speedFactorValue = user.mapSettings.speedFactor?.value;
  const trafficUserChange = user.mapSettings.traffic?.userChange;
  const trafficEnabled = user.mapSettings.traffic?.enabled;
  const trafficValue = user.mapSettings.traffic?.value;
  const isSolutionValid =
    solution && isIterationSolution(last(solution.requests[solution.show].iterations));
  const unassigned = tourData?.unassigned;
  const handleSetTourParameter = useCallback(
    (parameter) => dispatch(setTourParameter({ ...parameter, index: solution.show })),
    [dispatch, solution.show],
  );
  const handleSetSolutionParameter = useCallback(
    (data) => dispatch(setSolutionParam(data)),
    [dispatch],
  );
  const showUnassigned = tourData?.tours.length === 0 && tourData?.unassigned;

  const adjustDisplayedObject = useCallback(
    (option) => {
      if (errorState) setSelectedOption(OPTIONS.PROBLEM);
      else setSelectedOption(option);

      const lastIteration = solution && last(solution.requests[solution.show].iterations);
      if (option === OPTIONS.INTERNAL_ITERATIONS) setJsonObject(removeProperties(solution));
      else if (option === OPTIONS.INTERNAL_TOUR_PLANNER) setJsonObject(tourPlanner);
      else if (option === OPTIONS.INTERNAL_USER) setJsonObject(user);
      else if (option === OPTIONS.GEO)
        setJsonObject(adjustDownloadGeoJSON(lastIteration.geoJSON?.geo));
      else {
        const isSol = isIterationSolution(lastIteration);
        if (!lastIteration) setJsonObject({});
        else if (option === OPTIONS.PROBLEM && justPastedJson && !lastIteration.isProcessing) {
          setJsonObject(lastIteration.request.json);
          setJustPastedJson(false);
        } else if (option === OPTIONS.PROBLEM && !justPastedJson) {
          const problemJson = { ...lastIteration.request.json };
          if (
            problemJson.fleet &&
            Array.isArray(problemJson.fleet.types) &&
            problemJson.fleet.types[0]
          ) {
            const vehicle = tourPlanner.vehicles?.[0];
            const trafficVal = tourPlanner.traffic;
            const baseType = { ...problemJson.fleet.types[0] };
            const originalLimits = problemJson.fleet.types[0].limits || {};
            const newLimits = { ...originalLimits };

            if (user.mapSettings.speedFactor?.enabled || vehicle?.speedFactor) {
              if (typeof vehicle?.speedFactor !== 'undefined') {
                baseType.speedFactor = vehicle.speedFactor;
              }
            } else {
              delete baseType.speedFactor;
            }

            const hasTrafficSetting = user.mapSettings.traffic?.enabled;
            const vehicleTraffic =
              vehicle?.traffic && vehicle.traffic !== 'None'
                ? vehicle.traffic
                : trafficVal || undefined;

            const { traffic: _oldTraffic, ...restFleet } = problemJson.fleet;

            if (hasTrafficSetting || vehicleTraffic) {
              problemJson.fleet = { traffic: vehicleTraffic, ...restFleet };
            } else {
              problemJson.fleet = { ...restFleet };
            }

            if (user.mapSettings.addVehicleLimit) {
              const lim = vehicle?.limits;
              if (lim?.maxDistance?.enabled) {
                newLimits.maxDistance = lim.maxDistance.value;
              } else {
                delete newLimits.maxDistance;
              }

              if (lim?.shiftTime?.enabled) {
                newLimits.shiftTime = lim.shiftTime.value;
              } else {
                delete newLimits.shiftTime;
              }

              if (lim?.maxDrivingTime?.enabled) {
                newLimits.maxDrivingTime = lim.maxDrivingTime.value;
              } else {
                delete newLimits.maxDrivingTime;
              }

              const stops = { ...(originalLimits.stops || {}) };

              if (lim?.maxStops?.enabled) {
                stops.maxCount = { value: lim.maxStops.value };
              } else if (lim?.stops?.maxCount?.value !== undefined) {
                stops.maxCount = { value: lim.stops.maxCount.value };
              } else {
                delete stops.maxCount;
              }

              if (lim?.minStops?.enabled) {
                stops.minCount = { value: lim.minStops.value };
              } else if (lim?.stops?.minCount?.value !== undefined) {
                stops.minCount = { value: lim.stops.minCount.value };
              } else {
                delete stops.minCount;
              }

              if (Object.keys(stops).length) {
                newLimits.stops = stops;
              } else {
                delete newLimits.stops;
              }

              const hasStopsInfo =
                (lim?.maxStops && lim?.maxStops?.enabled) ||
                (lim?.minStops && lim?.minStops?.enabled) ||
                (lim?.stops &&
                  (lim?.stops?.maxCount?.value !== undefined ||
                    lim?.stops?.minCount?.value !== undefined));

              if (!hasStopsInfo) {
                delete newLimits.stops;
              }

              if (Object.keys(newLimits).length) {
                baseType.limits = newLimits;
              } else {
                delete baseType.limits;
              }
            } else if (
              originalLimits &&
              Object.keys(originalLimits).length &&
              !user.mapSettings.isUserChange
            ) {
              baseType.limits = originalLimits;
            } else {
              delete baseType.limits;
            }

            problemJson.fleet.types[0] = baseType;
          }

          if (problemJson.fleet?.types) {
            problemJson.fleet.types.forEach((type) => {
              if (type.shifts) {
                type.shifts.forEach((shift) => {
                  if (shift.start?.location) {
                    const latStr = shift.start.location.lat.toFixed(14);
                    const lngStr = shift.start.location.lng.toFixed(14);
                    shift.start.location.lat = parseFloat(latStr);
                    shift.start.location.lng = parseFloat(lngStr);
                  }
                  if (shift.end?.location) {
                    const latStr = shift.end.location.lat.toFixed(14);
                    const lngStr = shift.end.location.lng.toFixed(14);
                    shift.end.location.lat = parseFloat(latStr);
                    shift.end.location.lng = parseFloat(lngStr);
                  }
                });
              }
            });
          }

          if (!isEqual(jsonObject, problemJson) && !justPastedJson) {
            setJsonObject(problemJson);
            setJustPastedJson(false);
          }
        } else if (isSol && option === OPTIONS.SOLUTION && !errorState) {
          setJsonObject(
            removeProperties(
              lastIteration?.response ? lastIteration.response : getLastValidIteration(solution),
            ),
          );
        } else if (isSol && option === OPTIONS.SOLUTION && !errorState)
          setJsonObject(
            removeProperties(
              lastIteration?.response ? lastIteration.response : getLastValidIteration(solution),
            ),
          );
      }
    },
    [solution, tourPlanner, user, setJsonObject, setSelectedOption, errorState],
  );

  useEffect(() => {
    const lastIteration = solution && last(solution.requests[solution.show].iterations);
    let isSol = false;
    if (lastIteration) {
      isSol = isIterationSolution(lastIteration);
      setIsSolution(isSol);
    }

    if (
      (selectedOption === OPTIONS.SOLUTION || selectedOption === OPTIONS.PROBLEM) &&
      !solution.json &&
      !hasOngoingAsyncRequest(solution.requests)
    ) {
      const changeTo = isSol ? OPTIONS.SOLUTION : OPTIONS.PROBLEM;
      adjustDisplayedObject(changeTo);
    } else if (selectedOption === OPTIONS.INTERNAL_ITERATIONS) {
      adjustDisplayedObject(OPTIONS.INTERNAL_ITERATIONS);
    }
  }, [solution]);

  useEffect(() => {
    const lastIteration = solution && last(solution.requests[solution.show].iterations);
    const lastReqName = solution && solution.requests[solution.show]?.name;
    const methodsArr = Object.keys(METHODS).map((k) => METHODS[k]);
    const filename = lastReqName && !methodsArr.includes(lastReqName) ? `(${lastReqName})` : '';
    const problemName = solution.requests[solution.show]?.problemFilename;
    const problemFileName = problemName ? `(${problemName})` : filename;
    setFileNames({ filename, problemFileName });
    setHasProblem(!isSolution || (isSolution && lastIteration.request.json.fleet.types));
  }, [setHasProblem, isSolution, solution]);

  useEffect(() => {
    if (!showPanel || anyVehicleLimitUserChange || justPastedJson || showUnassigned) return;
    adjustDisplayedObject(isSolution ? OPTIONS.SOLUTION : OPTIONS.PROBLEM);
  }, [tourPlanner, user, showPanel, isSolution, solution, vehicleLimits, justPastedJson]);

  useEffect(() => {
    const shouldChange = vehicleLimits || anyVehicleLimitUserChange;
    adjustDisplayedObject(isSolution && !shouldChange ? OPTIONS.SOLUTION : OPTIONS.PROBLEM);
  }, [vehicleLimits, anyVehicleLimitUserChange]);

  const handleOnChangeOption = useCallback(
    (e) => {
      const value = e.target.value;
      const defaultSwitch = Object.values(OPTIONS).includes(value);
      const predefinedSelected = value.endsWith('.json');

      if (defaultSwitch) adjustDisplayedObject(value);
      else if (!predefinedSelected && !defaultSwitch) setPredefinedFile();
      else {
        setPredefinedFileName(value);
      }
    },
    [tourPlanner, user, solution],
  );

  const handlePredefinedFile = useCallback(
    (file) => {
      handleSetTourParameter({ ordersMode: METHODS.JSON_IMPORT });
      clearAll(dispatch, !user.hasStateMemory, false, solution.show);
      handleSetSolutionParameter({ param: { name: predefinedFileName } });
      handleSelectTourById({ selectedTour: display.selectedTour, noMapMovement: false });

      integrateFileText(
        JSON.stringify(file),
        'upload',
        'pasted',
        user,
        cookies,
        oAuth,
        dispatch,
        true,
        error,
        solution.show,
        false,
      );
      setPredefinedFileName();
      setPredefinedFile();
    },
    [
      error,
      cookies,
      dispatch,
      oAuth,
      user,
      handleSetSolutionParameter,
      handleSetTourParameter,
      solution.show,
      predefinedFileName,
      display.selectedTour,
    ],
  );

  useEffect(() => {
    if (predefinedFileName) {
      getPredefinedFiles(
        predefinedFileName,
        setPredefinedFile,
        setPredefinedFileName,
        dispatch,
        error,
      );
    }
  }, [predefinedFileName, dispatch, error]);

  // Fetch dynamic predefined files list on mount
  useEffect(() => {
    const loadFileList = async () => {
      const files = await fetchPredefinedFileList();
      setPredefinedFiles(files);
    };
    loadFileList();
  }, []);

  useEffect(() => {
    if (currentStep === 1 && showPanel) {
      clearAll(
        dispatch,
        false,
        false,
        solution.show,
        tourPlanner.uploadedLocations,
        tourPlanner.vehicles[0]?.limits,
      );
    }
  }, [currentStep, showPanel]);

  useEffect(() => {
    if (predefinedFile) {
      handlePredefinedFile(predefinedFile);
    }
  }, [handlePredefinedFile, predefinedFile]);

  useEffect(() => {
    if (tourData?.tours.length === 0 && tourData?.unassigned)
      adjustDisplayedObject(OPTIONS.PROBLEM);
  }, [tourData?.tours?.length, tourData?.unassigned]);

  useEffect(() => {
    adjustDisplayedObject(OPTIONS.PROBLEM);
  }, [convertToTerritory]);

  useEffect(() => {
    if (sfUserChange && isSolutionValid) adjustDisplayedObject(OPTIONS.PROBLEM);
  }, [speedFactor, speedFactorValue, sfUserChange, isSolutionValid]);

  useEffect(() => {
    if (trafficUserChange && isSolutionValid) adjustDisplayedObject(OPTIONS.PROBLEM);
  }, [trafficEnabled, trafficValue, trafficUserChange, isSolutionValid]);

  useEffect(() => {
    const territoriesEnabled = user.mapSettings?.territories?.enabled;
    const skillsEnabled = user.mapSettings?.skills?.enabled;
    const categoriesEnabled = user.mapSettings?.categories?.enabled;
    const positionChanged = user.mapSettings?.positionsChange;
    const groupsChanged = user.mapSettings?.groupsChange;
    const priorityChanged = user.mapSettings?.priorityChange;
    const maxTimeChanged = user.mapSettings?.maxTimeChange;

    if (
      isSolutionValid &&
      (territoriesEnabled ||
        skillsEnabled ||
        categoriesEnabled ||
        positionChanged ||
        groupsChanged ||
        priorityChanged ||
        maxTimeChanged)
    ) {
      adjustDisplayedObject(OPTIONS.PROBLEM);
    }
  }, [
    user.mapSettings?.territories?.enabled,
    user.mapSettings?.skills?.enabled,
    user.mapSettings?.categories?.enabled,
    user.mapSettings?.positionsChange,
    user.mapSettings?.groupsChange,
    user.mapSettings?.maxTimeChange,
    user.mapSettings?.priorityChange,
    isSolutionValid,
  ]);

  useEffect(() => {
    if (anyVehicleLimitUserChange && isSolutionValid) adjustDisplayedObject(OPTIONS.PROBLEM);
  }, [
    vehicleLimits,
    tourPlanner.vehicles?.[0]?.limits?.maxDistance,
    tourPlanner.vehicles?.[0]?.limits?.shiftTime,
    tourPlanner.vehicles?.[0]?.limits?.maxDrivingTime,
    tourPlanner.vehicles?.[0]?.limits?.maxStops,
    tourPlanner.vehicles?.[0]?.limits?.minStops,
    tourPlanner.vehicles?.[0]?.limits?.stops,
    anyVehicleLimitUserChange,
    isSolutionValid,
  ]);

  useEffect(() => {
    if (unassigned?.length > 0) {
      adjustDisplayedObject(OPTIONS.PROBLEM);
    }
  }, [unassigned]);

  useEffect(() => {
    const vehicle = tourPlanner.vehicles?.[0];
    const lmts = vehicle?.limits || {};

    const anyLimitChanged =
      lmts.maxDistance?.userChange ||
      lmts.shiftTime?.userChange ||
      lmts.maxDrivingTime?.userChange ||
      lmts.maxStops?.userChange ||
      lmts.minStops?.userChange ||
      lmts.stops?.userChange;

    if (anyLimitChanged) {
      adjustDisplayedObject(OPTIONS.PROBLEM);
    }
  }, [
    tourPlanner.vehicles?.[0]?.limits?.maxDistance?.userChange,
    tourPlanner.vehicles?.[0]?.limits?.shiftTime?.userChange,
    tourPlanner.vehicles?.[0]?.limits?.maxDrivingTime?.userChange,
    tourPlanner.vehicles?.[0]?.limits?.maxStops?.userChange,
    tourPlanner.vehicles?.[0]?.limits?.minStops?.userChange,
    tourPlanner.vehicles?.[0]?.limits?.stops?.userChange,
    adjustDisplayedObject,
  ]);

  useEffect(() => {
    setJustPastedJson(false);
  }, [solution.jsonNoPaste]);

  return (
    <>
      <StyledCloseButton onClick={() => togglePanel()} data-test-id="closeJsonViewer" />
      <StyledContrastPanelHeader size="0">
        <StyledWrapper>
          <StyledText bold contrast jsonTitle>
            {configTitle}
          </StyledText>
          {!isProd && (
            <StyledButtonSmall
              contrast={showPredefined}
              selected={!showPredefined}
              predefinedBtn
              onClick={() => setShowPredefined(!showPredefined)}
            >
              {showPredefined ? hidePredefinedFiles : showPredefinedFiles}
            </StyledButtonSmall>
          )}
        </StyledWrapper>
        <StyledContrastPanelHeaderText paddingBottom jsonTitle={isProd}>
          {configDesc}
          <br />
          {isProd && <i>{solutionUpload}</i>}
        </StyledContrastPanelHeaderText>
        <StyledSelectInput
          data-test-id="JSONViewerDropdown"
          contrast
          onChange={handleOnChangeOption}
          value={selectedOption}
        >
          <option value={OPTIONS.PROBLEM} key={OPTIONS.PROBLEM} disabled={!hasProblem}>
            {`${optionProblem} ${fileNames.problemFileName}`}
          </option>
          <option value={OPTIONS.SOLUTION} key={OPTIONS.SOLUTION} disabled={!isSolution}>
            {`${optionSolution} ${fileNames.filename}`}
          </option>
          <option value={OPTIONS.GEO} key={OPTIONS.GEO} disabled={!isSolution}>
            {`${optionGeo} ${fileNames.filename}`}
          </option>
          {showFullDev && (
            <>
              <option value={OPTIONS.INTERNAL_TOUR_PLANNER} key={OPTIONS.INTERNAL_TOUR_PLANNER}>
                Internal - Tour Planner
              </option>
              <option value={OPTIONS.INTERNAL_USER} key={OPTIONS.INTERNAL_USER}>
                Internal - User settings
              </option>
              <option value={OPTIONS.INTERNAL_ITERATIONS} key={OPTIONS.INTERNAL_ITERATIONS}>
                Internal - All planning iterations
              </option>
            </>
          )}
          {!isProd &&
            showPredefined &&
            PREDEFINED_FILES.map((file) => (
              <StyledOption value={file} key={file}>
                {file}
              </StyledOption>
            ))}
        </StyledSelectInput>
      </StyledContrastPanelHeader>
    </>
  );
};

export default withTranslation(DevConsoleHeader);
